/**
 * Cross-service inbound contract: app-integration (publisher) → conversation
 * (consumer). The client edge derives a stable `conversationId` + an
 * `idempotencyKey` from the authenticated user + the client message id (T4), so
 * a client retry maps to the same conversation and dedups at the conversation
 * service instead of spawning a new thread (the v1 bug).
 *
 * Distinct from `MessageEnvelopeSchema` (the rich multi-channel v1 inbound) and
 * from `AppChannelInboundRequestSchema` (the raw HTTP body the WebView posts):
 * this is the lean app→conversation bus frame the v2 stack publishes after the
 * edge has resolved identity + idempotency. `.strict()` at the top level surfaces
 * unexpected keys as parse errors at the consumer.
 */

import { z } from 'zod';

import { AppChannelConfirmationSchema, AppChannelSelectionSchema } from './app_channel_request';
import { ConversationIdSchema, MessageIdSchema } from './message_envelope';
import { EnvelopeOrgIdSchema } from './org_id';

export const ConversationInboundSchema = z
  .object({
    version: z.literal(1),
    conversationId: ConversationIdSchema,
    orgId: EnvelopeOrgIdSchema,
    messageId: MessageIdSchema,
    channel: z.literal('app'),
    // Vazio só no requery silencioso de card (confirmation estruturada presente)
    // — validado no refine do final; espelha `AppChannelInboundRequestSchema`.
    text: z.string().max(4000),
    /** Stable sha256(sub:clientMessageId) — the dedup key across the chain. */
    idempotencyKey: z.string().min(1).max(128),
    at: z.iso.datetime(),
    // Opaque structured submit (drawer / offer-pick) forwarded from app-int. The
    // conversation aggregate never inspects it — it rides to agents on
    // `metadata.submit`. Absent ⇒ a plain text turn. Validated for shape only
    // (the genui selections/traveler stay opaque, narrowed in agents-v2).
    structured: z
      .object({
        confirmation: AppChannelConfirmationSchema.optional(),
        selection: AppChannelSelectionSchema.optional(),
      })
      .optional(),
    // The VERIFIED app-channel customer identity (Cognito sub + optional VOLL
    // userId), set by app-integration from the validated chat token. NEVER trust
    // an unverified sub. Absent when no authenticated identity is available
    // (defensive: omit rather than forward an unverified value).
    sender: z
      .object({ sub: z.string().min(1).max(256), userId: z.string().min(1).max(256).optional() })
      .strict()
      .optional(),
  })
  .strict()
  .refine((v) => v.text.length > 0 || v.structured?.confirmation !== undefined, {
    message: 'text must be non-empty unless a structured confirmation is present',
    path: ['text'],
  });

export type ConversationInbound = z.infer<typeof ConversationInboundSchema>;
